import { useLocation, Link } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'

const ROUTE_LABELS: Record<string, string> = {
  '': 'Home',
  build: 'Build',
  dashboard: 'Dashboard',
  projects: 'Projects',
  settings: 'Settings',
  marketing: 'Marketing',
  campaigns: 'Campaigns',
  calendar: 'Content Calendar',
  leads: 'Lead Pipeline',
  billing: 'Billing',
  team: 'Team',
  notifications: 'Notifications',
  'token-usage': 'Token Usage',
  'audit-log': 'Audit Log',
  knowledge: 'Knowledge',
  connectors: 'Connectors',
  analytics: 'Analytics',
  nurture: 'Nurture',
  'ab-tests': 'A/B Tests',
  edit: 'Edit',
  tokens: 'Token Usage',
  rules: 'Executive Rules',
  'model-settings': 'Model & Billing Settings',
}

export function Breadcrumbs() {
  const location = useLocation()
  const segments = location.pathname.split('/').filter(Boolean)

  if (segments.length <= 1) return null

  // /build/* owns its own identity surface (the sidebar workspace switcher
  // + the build sidebar's Project/Application selectors). A generic
  // "Build > Details > Details > Bld …" crumb chain is just noise — hide
  // it entirely on build paths.
  if (segments[0] === 'build') return null

  const crumbs = segments.map((segment, index) => {
    const path = '/' + segments.slice(0, index + 1).join('/')
    const isLast = index === segments.length - 1
    const isId = /^[0-9a-f-]{36}$/.test(segment) || /^[0-9a-f]{8,}$/.test(segment)
    const label = isId ? 'Details' : (ROUTE_LABELS[segment] || formatSegment(segment))
    return { path, label, isLast, isId }
  })

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-gray-400 mt-3 mb-4">
      <Link
        to="/"
        className="flex items-center gap-1 hover:text-white transition-colors"
        aria-label="Home"
      >
        <Home className="w-3 h-3" aria-hidden="true" />
      </Link>
      {crumbs.map((crumb) => (
        <span key={crumb.path} className="flex items-center gap-1">
          <ChevronRight className="w-3 h-3 text-gray-400" aria-hidden="true" />
          {crumb.isLast ? (
            <span aria-current="page" className="text-gray-200">{crumb.label}</span>
          ) : (
            <Link
              to={crumb.path}
              className="hover:text-white transition-colors"
            >
              {crumb.label}
            </Link>
          )}
        </span>
      ))}
    </nav>
  )
}

function formatSegment(segment: string): string {
  // Convert UUID-like segments to short form
  if (/^[0-9a-f]{8}-/.test(segment)) {
    return 'Details'
  }
  // Convert kebab-case to Title Case
  return segment
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}
